import { useState, useMemo } from 'react';
import {
  collection, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { Button, Modal, ModalBanner } from './ui';
import { fmtMoney } from '../utils/currency';
import { shortInvoiceNo } from '../utils/invoiceNo';
import {
  buildEntry, postEntry, entryId, linesSupplierAdvanceApply, linesCustomerAdvanceApply,
} from '../utils/ledger';
import type { SupplierAdvance, CustomerAdvance, PaymentStatus } from '../types';

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// Minimal invoice shape both PurchaseInvoice and SaleInvoice satisfy.
export interface ApplyInvoice {
  id: string; invoiceNo: string; totalAmountUSD: number;
  paidUSD?: number; creditedUSD?: number;
  saleDate?: string; purchaseDate?: string; dueDate?: string;
}
function fmtDay(d?: string) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
const daysBetween = (a: string, b: string) =>
  Math.round((new Date(b + 'T00:00:00').getTime() - new Date(a + 'T00:00:00').getTime()) / 86400000);

/** Equal shares of `pool` over the invoices, never above what each still owes;
 *  what a small invoice can't take is shared out among the others. */
function splitEqually(pool: number, outs: { id: string; out: number }[]): Record<string, number> {
  const res: Record<string, number> = {};
  let left = pool;
  let open = outs.filter(o => o.out > 0.005);
  while (open.length > 0 && left > 0.005) {
    const share = left / open.length;
    const capped = open.filter(o => o.out - (res[o.id] ?? 0) <= share + 1e-9);
    if (capped.length === 0) {
      open.forEach(o => { res[o.id] = (res[o.id] ?? 0) + share; });
      left = 0;
      break;
    }
    capped.forEach(o => { left -= o.out - (res[o.id] ?? 0); res[o.id] = o.out; });
    open = open.filter(o => !capped.includes(o));
  }
  // Round to cents; any rounding cent goes on the first open row.
  const ids = Object.keys(res);
  ids.forEach(id => { res[id] = round2(res[id]); });
  const diff = round2(Math.min(pool, outs.reduce((s, o) => s + o.out, 0)) - ids.reduce((s, id) => s + res[id], 0));
  if (ids.length && Math.abs(diff) >= 0.01) res[ids[0]] = round2(res[ids[0]] + diff);
  return res;
}

export default function ApplyAdvanceModal({ kind, advance, invoices, partyName, onClose }: {
  kind: 'supplier' | 'customer';
  /** The customer / supplier's name, shown in the header. */
  partyName?: string;
  advance: SupplierAdvance | CustomerAdvance;
  invoices: ApplyInvoice[];   // already filtered to this advance's entity, outstanding > 0
  onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const isSup = kind === 'supplier';
  const entityId = isSup ? (advance as SupplierAdvance).fromEntityId : (advance as CustomerAdvance).toEntityId;

  const [date, setDate] = useState(todayISO());
  const [amt, setAmt]   = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const remaining = round2((advance.amountUSD ?? 0) - (advance.appliedUSD ?? 0));
  const outstandingOf = (inv: ApplyInvoice) =>
    round2(Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0)));

  const allocated = useMemo(
    () => round2(invoices.reduce((s, inv) => s + (parseFloat(amt[inv.id]) || 0), 0)),
    [invoices, amt],
  );
  const overAllocated = allocated > remaining + 0.005;

  async function save() {
    const rows = invoices
      .map(inv => ({ inv, amount: round2(parseFloat(amt[inv.id]) || 0) }))
      .filter(r => r.amount > 0.005);
    if (rows.length === 0) return setError('Enter an amount to apply against at least one invoice.');
    if (overAllocated) return setError(`You've allocated ${money(allocated)} but only ${money(remaining)} is left on this advance.`);
    for (const r of rows) {
      if (r.amount > outstandingOf(r.inv) + 0.005) return setError(`${r.inv.invoiceNo}: can't apply more than its ${money(outstandingOf(r.inv))} outstanding.`);
    }

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const advColl = isSup ? 'supplierAdvances' : 'customerAdvances';
    const invColl = isSup ? 'invoices' : 'sales';
    const advRef  = doc(db, advColl, advance.id);
    const appRefs = rows.map(() => doc(collection(db, advColl, advance.id, 'applications')));

    try {
      await runTransaction(db, async tx => {
        const advSnap = await tx.get(advRef);
        const invSnaps = await Promise.all(rows.map(r => tx.get(doc(db, invColl, r.inv.id))));
        if (!advSnap.exists()) throw new Error('That advance no longer exists.');
        const adv = advSnap.data() as SupplierAdvance | CustomerAdvance;
        const freshRemaining = round2((adv.amountUSD ?? 0) - (adv.appliedUSD ?? 0));
        const total = round2(rows.reduce((s, r) => s + r.amount, 0));
        if (total > freshRemaining + 0.005) throw new Error(`Only ${money(freshRemaining)} is left on this advance now — please reload.`);

        rows.forEach((r, i) => {
          const s = invSnaps[i];
          if (!s.exists()) throw new Error(`${r.inv.invoiceNo} no longer exists.`);
          const inv = s.data() as { totalAmountUSD: number; paidUSD?: number; creditedUSD?: number };
          const freshOut = round2(Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0)));
          if (r.amount > freshOut + 0.005) throw new Error(`${r.inv.invoiceNo}: only ${money(freshOut)} outstanding now — please reload.`);
          const newPaid = round2((inv.paidUSD ?? 0) + r.amount);
          const settled = newPaid + (inv.creditedUSD ?? 0);
          const status: PaymentStatus =
            settled >= inv.totalAmountUSD - 0.005 ? 'paid' : settled > 0 ? 'partial' : 'unpaid';

          tx.set(appRefs[i], {
            advanceId: advance.id, invoiceId: r.inv.id, invoiceNo: r.inv.invoiceNo,
            amountUSD: r.amount, date,
            createdAt: serverTimestamp(), createdBy: user!.id,
          });
          tx.update(doc(db, invColl, r.inv.id), { paidUSD: newPaid, paymentStatus: status, updatedAt: serverTimestamp() });
          postEntry(tx, db, buildEntry({
            businessId: business.id,
            sourceType: isSup ? 'supplier_advance_apply' : 'customer_advance_apply',
            sourceId: appRefs[i].id,
            sourceCollection: `${advColl}/${advance.id}/applications`,
            date, currency: advance.currency, createdBy: user!.id,
            note: `Advance ${advance.advanceNo} applied to ${r.inv.invoiceNo}`,
            lines: isSup
              ? linesSupplierAdvanceApply(r.amount, entityId)
              : linesCustomerAdvanceApply(r.amount, entityId),
          }), entryId(isSup ? 'supplier_advance_apply' : 'customer_advance_apply', appRefs[i].id));
        });

        const newApplied = round2((adv.appliedUSD ?? 0) + total);
        tx.update(advRef, {
          appliedUSD: newApplied,
          status: newApplied >= (adv.amountUSD ?? 0) - 0.005 ? 'used' : newApplied > 0 ? 'partial' : 'open',
          updatedAt: serverTimestamp(),
        });
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not apply the advance.');
    }
    setSaving(false);
  }

  // Oldest due first: the order the table shows and "Oldest first" fills.
  const ordered = [...invoices].sort((x, y) =>
    (x.dueDate ?? '9999').localeCompare(y.dueDate ?? '9999') || (x.saleDate ?? x.purchaseDate ?? '').localeCompare(y.saleDate ?? y.purchaseDate ?? ''));
  const usedBefore = round2(advance.appliedUSD ?? 0);
  const leftAfter  = round2(remaining - allocated);
  const today = todayISO();
  const fmtAmt = (n: number) => (n > 0.005 ? n.toFixed(2) : '');

  // Quick fills.
  function fillOldestFirst() {
    let pool = remaining;
    const next: Record<string, string> = {};
    ordered.forEach(inv => {
      const take = round2(Math.max(0, Math.min(outstandingOf(inv), pool)));
      pool = round2(pool - take);
      next[inv.id] = fmtAmt(take);
    });
    setAmt(next);
  }
  function fillEqually() {
    const res = splitEqually(remaining, ordered.map(inv => ({ id: inv.id, out: outstandingOf(inv) })));
    setAmt(Object.fromEntries(ordered.map(inv => [inv.id, fmtAmt(res[inv.id] ?? 0)])));
  }
  function fillFull(inv: ApplyInvoice) {
    const others = round2(allocated - (parseFloat(amt[inv.id]) || 0));
    const take = round2(Math.max(0, Math.min(outstandingOf(inv), remaining - others)));
    setAmt(a => ({ ...a, [inv.id]: fmtAmt(take) }));
  }

  const sumTotal = round2(ordered.reduce((s2, inv) => s2 + inv.totalAmountUSD, 0));
  const sumOut   = round2(ordered.reduce((s2, inv) => s2 + outstandingOf(inv), 0));
  const quickBtn: React.CSSProperties = { height: 32, boxSizing: 'border-box' };

  return (
    <Modal open onClose={onClose} size="xl" hideHead
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving} disabled={allocated <= 0 || overAllocated}>
              Apply {allocated > 0 ? money(allocated) : ''}
            </Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={partyName || (isSup ? 'Supplier Advance' : 'Customer Advance')}
        eyebrow={`Apply ${isSup ? 'Supplier' : 'Customer'} Advance`}
        right={
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>Advance No</div>
            <span title={advance.advanceNo} className="sm-ref">{shortInvoiceNo(advance.advanceNo)}</span>
          </div>
        }
        onClose={onClose}
      />

      {/* Where the advance stands, and what this apply does to it. */}
      <div className="adv-sum">
        <div><span>Advance</span><strong>{money(advance.amountUSD)}</strong></div>
        <div><span>Used before</span><strong>{money(usedBefore)}</strong></div>
        <div><span>Left now</span><strong>{money(remaining)}</strong></div>
        <div><span>Applying</span><strong style={{ color: overAllocated ? 'var(--danger)' : 'var(--primary)' }}>{money(allocated)}</strong></div>
        <div><span>Left after</span><strong style={{ color: leftAfter < -0.005 ? 'var(--danger)' : leftAfter > 0.005 ? 'var(--text-1)' : 'var(--success)' }}>{money(leftAfter)}</strong></div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', margin: '14px 0 10px' }}>
        <div className="form-group" style={{ margin: 0, width: 180 }}>
          <label>Apply Date</label>
          <input type="date" value={date} max={today} onChange={e => setDate(e.target.value)} />
        </div>
        {invoices.length > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick fill</span>
            <Button size="sm" variant="secondary" style={quickBtn} onClick={fillOldestFirst} title="Use the advance on the oldest due invoices first">Oldest first</Button>
            <Button size="sm" variant="secondary" style={quickBtn} onClick={fillEqually} title="Share the advance equally over every invoice (never more than one owes)">Split equally</Button>
            <Button size="sm" variant="ghost" style={quickBtn} onClick={() => setAmt({})} disabled={allocated <= 0}>Clear</Button>
          </div>
        )}
      </div>

      {invoices.length === 0 ? (
        <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-3)', border: '1px dashed var(--border)', borderRadius: 'var(--r-sm)' }}>
          This {isSup ? 'supplier' : 'customer'} has no unpaid invoices to apply the advance to.
        </div>
      ) : (
        <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'auto', maxHeight: 360, overscrollBehavior: 'contain' }}>
          <table className="adv-apply-table">
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: 'right' }}>#</th>
                <th style={{ width: 110 }}>Invoice No</th>
                <th style={{ width: 120 }}>Date</th>
                <th style={{ width: 150 }}>Due Date</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
                <th style={{ textAlign: 'right', width: 220 }}>Apply</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((inv, i) => {
                const out = outstandingOf(inv);
                const val = parseFloat(amt[inv.id]) || 0;
                const over = val > out + 0.005;
                const late = inv.dueDate && inv.dueDate < today ? daysBetween(inv.dueDate, today) : 0;
                return (
                  <tr key={inv.id} className={val > 0.005 ? 'adv-row-on' : undefined}>
                    <td style={{ textAlign: 'right', color: 'var(--text-3)', fontFamily: 'var(--font-num)' }}>{i + 1}</td>
                    <td><span className="sm-ref" title={inv.invoiceNo}>{shortInvoiceNo(inv.invoiceNo)}</span></td>
                    <td style={{ fontFamily: 'var(--font-num)' }}>{fmtDay(inv.saleDate ?? inv.purchaseDate)}</td>
                    <td style={{ fontFamily: 'var(--font-num)', color: late ? 'var(--danger)' : undefined }}>
                      {fmtDay(inv.dueDate)}
                      {late > 0 && <span className="adv-late">{late}d late</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(inv.totalAmountUSD)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>{money(out)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <input type="number" step="0.01" min="0" max={Math.min(out, remaining)} className="no-spinner"
                          value={amt[inv.id] ?? ''} onChange={e => setAmt(a => ({ ...a, [inv.id]: e.target.value }))}
                          placeholder="0.00" title={over ? `More than the ${money(out)} outstanding` : undefined}
                          style={{
                            width: 130, textAlign: 'right', height: 32, boxSizing: 'border-box', fontSize: 12,
                            fontFamily: 'var(--font-num)', padding: '0 8px', color: 'var(--text-1)',
                            border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)',
                            ...(over ? { borderColor: 'var(--danger)', background: 'color-mix(in srgb, var(--danger) 8%, var(--surface))' } : {}),
                          }} />
                        <button type="button" className="adv-full-btn" onClick={() => fillFull(inv)}
                          disabled={leftAfter <= 0.005 && val <= 0.005}
                          title="Fill what this invoice owes (up to what's left on the advance)">Full</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Total · {ordered.length} invoice{ordered.length !== 1 ? 's' : ''}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(sumTotal)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(sumOut)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', color: overAllocated ? 'var(--danger)' : 'var(--primary)', paddingRight: 62 }}>{money(allocated)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {overAllocated && (
        <p className="form-hint" style={{ color: 'var(--danger)', fontWeight: 600, marginTop: 8 }}>
          That's {money(allocated - remaining)} more than is left on the advance. Lower an amount or press Clear.
        </p>
      )}
    </Modal>
  );
}
